import { useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { Check, Globe, Loader2, Lock, X } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useStandardAgentStore } from "@/store/standardAgentStore";
import { fetchApi } from "@/lib/api";
import { toast } from "sonner";
import {
  PublicUserSchema,
  THREAD_VISIBILITY,
  formatUserName,
  type PublicUser,
  type ThreadVisibility,
  type ThreadVisibilityResponse,
} from "@forgepoint/shared";
import { cn } from "@/lib/utils";

/** Props for the ThreadVisibilityDropdown component. */
interface ThreadVisibilityDropdownProps {
  threadId: string;
  visibility: ThreadVisibility;
  isOwner: boolean;
  ownerId: string;
  visibilityData: ThreadVisibilityResponse | undefined;
}

const VISIBILITY_OPTIONS = [
  {
    value: THREAD_VISIBILITY.ORGANIZATION,
    label: "Organization",
    description:
      "Anyone in your organization can open it. The agent can create and edit records.",
    Icon: Globe,
    iconTint: "text-info",
  },
  {
    value: THREAD_VISIBILITY.PRIVATE,
    label: "Private",
    description:
      "Only you and people you add can open it. The agent can't create or edit records.",
    Icon: Lock,
    iconTint: "text-warning",
  },
] as const;

/**
 * "Share" control for a thread: a labeled trigger button showing the current
 * visibility glyph, opening a popover with the visibility options and, when
 * private, the per-user access list. While the visibility data is still loading
 * (or failed to load), the private member list and add-people editor are gated
 * behind a loading state instead of rendering from an assumed-empty list.
 * Non-owners get a read-only view.
 * @param props - The component props
 * @returns The share button with its access popover
 */
export function ThreadVisibilityDropdown({
  threadId,
  visibility,
  isOwner,
  ownerId,
  visibilityData,
}: ThreadVisibilityDropdownProps) {
  const updateThreadVisibility = useStandardAgentStore(
    (state) => state.updateThreadVisibility,
  );
  const addThreadVisibilityUser = useStandardAgentStore(
    (state) => state.addThreadVisibilityUser,
  );
  const removeThreadVisibilityUser = useStandardAgentStore(
    (state) => state.removeThreadVisibilityUser,
  );
  const isUpdating = useStandardAgentStore(
    (state) => state.threadVisibilityUpdating.get(threadId) ?? false,
  );

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingConfirmVisibility, setPendingConfirmVisibility] =
    useState<ThreadVisibility | null>(null);
  const [pendingVisibility, setPendingVisibility] =
    useState<ThreadVisibility | null>(null);
  const [orgUsers, setOrgUsers] = useState<PublicUser[]>([]);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [addingUserId, setAddingUserId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!popoverOpen) {
      return;
    }
    fetchApi("users/members", "GET").then((response) => {
      if (response.ok && response.data) {
        const parsed = z
          .object({ users: z.array(PublicUserSchema) })
          .parse(response.data);
        setOrgUsers(parsed.users);
      }
    });
  }, [popoverOpen]);

  const isPrivate = visibility === THREAD_VISIBILITY.PRIVATE;
  const isVisibilityDataLoaded = visibilityData !== undefined;
  const visibilityUsers = useMemo(
    () => visibilityData?.users ?? [],
    [visibilityData],
  );

  const ownerInVisibilityList = useMemo(
    () => visibilityUsers.some((user) => user.id === ownerId),
    [visibilityUsers, ownerId],
  );

  const memberCount = ownerInVisibilityList
    ? visibilityUsers.length
    : visibilityUsers.length + 1;

  const displayedUsers = useMemo(() => {
    if (ownerInVisibilityList) {
      return visibilityUsers;
    }
    const ownerUser = orgUsers.find((user) => user.id === ownerId);
    return ownerUser ? [ownerUser, ...visibilityUsers] : visibilityUsers;
  }, [visibilityUsers, ownerId, ownerInVisibilityList, orgUsers]);

  const availableUsers = useMemo(() => {
    const existingUserIds = new Set(displayedUsers.map((user) => user.id));
    return orgUsers.filter(
      (user) => !existingUserIds.has(user.id) && user.id !== ownerId,
    );
  }, [orgUsers, displayedUsers, ownerId]);

  const applyVisibility = useCallback(
    async (target: ThreadVisibility) => {
      setPendingVisibility(target);
      const success = await updateThreadVisibility(threadId, target);
      setPendingVisibility(null);
      if (!success) {
        toast.error("Failed to update thread visibility");
      }
    },
    [threadId, updateThreadVisibility],
  );

  const handleSelect = useCallback(
    (newVisibility: ThreadVisibility) => {
      if (!isOwner || isUpdating || newVisibility === visibility) {
        return;
      }
      setPendingConfirmVisibility(newVisibility);
      setConfirmOpen(true);
    },
    [isOwner, isUpdating, visibility],
  );

  const handleConfirmVisibility = useCallback(() => {
    if (!pendingConfirmVisibility) {
      return;
    }
    setConfirmOpen(false);
    applyVisibility(pendingConfirmVisibility);
  }, [pendingConfirmVisibility, applyVisibility]);

  const handleAddUser = useCallback(
    async (userId: string) => {
      setAddingUserId(userId);
      const success = await addThreadVisibilityUser(threadId, userId);
      if (!success) {
        toast.error("Failed to add user");
      }
      setAddingUserId(null);
      setSearchQuery("");
    },
    [threadId, addThreadVisibilityUser],
  );

  const handleRemoveUser = useCallback(
    async (userId: string) => {
      setRemovingUserId(userId);
      const error = await removeThreadVisibilityUser(threadId, userId);
      if (error) {
        toast.error(error);
      }
      setRemovingUserId(null);
    },
    [threadId, removeThreadVisibilityUser],
  );

  const handlePopoverOpenChange = useCallback((open: boolean) => {
    setPopoverOpen(open);
    if (!open) {
      setSearchQuery("");
    }
  }, []);

  const TriggerIcon = isPrivate ? Lock : Globe;
  const privateAccessSummary = isVisibilityDataLoaded
    ? `${memberCount} ${memberCount === 1 ? "person has" : "people have"} access`
    : "Loading access…";
  const triggerAriaLabel = isPrivate
    ? `Share thread. Current access: Private, ${
        isVisibilityDataLoaded ? privateAccessSummary : "loading access"
      }`
    : "Share thread. Current access: Everyone in your organization";
  const accessSummary = isPrivate
    ? privateAccessSummary
    : "Anyone in your organization can open it";

  const showAddPeopleDropdown =
    searchQuery.length > 0 && availableUsers.length > 0;

  const confirmToPrivate =
    pendingConfirmVisibility === THREAD_VISIBILITY.PRIVATE;
  const ConfirmIcon = confirmToPrivate ? Lock : Globe;
  const confirmIconBubbleClass = confirmToPrivate
    ? "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400"
    : "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-400";
  const confirmTitle = confirmToPrivate
    ? "Switch to Private?"
    : "Switch to Organization?";
  const confirmDescription = confirmToPrivate
    ? "Only you and the people you add can see this thread. The agent can no longer create or edit records from it."
    : "Everyone in your organization can see this thread. The agent can create and edit records from it.";
  const confirmActionLabel = confirmToPrivate
    ? "Switch to Private"
    : "Switch to Organization";

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label={triggerAriaLabel}
            aria-haspopup="dialog"
            className="h-[30px] gap-1.5 rounded-md px-3 text-xs font-medium"
          >
            <TriggerIcon
              className={cn(
                "h-3.5 w-3.5",
                isPrivate ? "text-warning" : "text-info",
              )}
            />
            <span className="hidden sm:inline">Share</span>
            {isUpdating && <Loader2 className="h-3 w-3 animate-spin" />}
          </Button>
        </PopoverTrigger>

        <PopoverContent align="end" sideOffset={6} className="w-[340px] p-0">
          <div className="px-4 pb-3 pt-4">
            <p className="text-sm font-medium">Who can access this thread</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {accessSummary}
            </p>
          </div>

          <RadioGroup
            value={visibility}
            onValueChange={(value) => handleSelect(value as ThreadVisibility)}
            disabled={!isOwner || isUpdating}
            className="gap-0 px-2 pb-2"
          >
            {VISIBILITY_OPTIONS.map((option) => {
              const isActive = visibility === option.value;
              const isPending = pendingVisibility === option.value;
              return (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    "flex items-start gap-2.5 rounded-md px-2 py-2",
                    isOwner && !isUpdating
                      ? "cursor-pointer hover:bg-muted/50"
                      : "cursor-default",
                  )}
                >
                  <RadioGroupItem
                    value={option.value}
                    id={`thread-visibility-${option.value}`}
                    aria-label={option.label}
                    className="mt-0.5 shrink-0"
                  />
                  <option.Icon
                    className={cn("mt-0.5 h-4 w-4 shrink-0", option.iconTint)}
                  />
                  <div className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">
                      {option.label}
                    </span>
                    <p className="text-xs text-muted-foreground">
                      {option.description}
                    </p>
                  </div>
                  {isPending ? (
                    <Loader2 className="mt-[3px] h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
                  ) : (
                    isActive && (
                      <Check className="mt-[3px] h-3.5 w-3.5 shrink-0 text-foreground" />
                    )
                  )}
                </div>
              );
            })}
          </RadioGroup>

          {isPrivate && !isVisibilityDataLoaded && (
            <div className="flex items-center justify-center border-t px-4 py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}

          {isPrivate && isVisibilityDataLoaded && (
            <>
              <div className="border-t">
                <div className="px-4 py-2">
                  <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    {memberCount} member{memberCount !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="max-h-[200px] overflow-y-auto px-2 pb-2">
                  {displayedUsers.map((user) => {
                    const isOwnerRow = user.id === ownerId;
                    const displayName = formatUserName(
                      user.firstName,
                      user.lastName,
                      user.email,
                    );
                    const isRemoving = removingUserId === user.id;
                    return (
                      <div
                        key={user.id}
                        className={cn(
                          "group flex items-center justify-between gap-2 rounded-md px-2 py-1.5 transition-colors",
                          !isOwnerRow && "hover:bg-muted/50",
                        )}
                      >
                        <div className="flex min-w-0 flex-col">
                          <span className="whitespace-normal text-sm font-medium text-foreground [overflow-wrap:anywhere]">
                            {displayName}
                          </span>
                          {(user.firstName || user.lastName) && (
                            <span className="whitespace-normal text-[11px] text-muted-foreground [overflow-wrap:anywhere]">
                              {user.email}
                            </span>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          {isOwnerRow && (
                            <Badge
                              variant="secondary"
                              className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                            >
                              Owner
                            </Badge>
                          )}
                          {!isOwnerRow && isOwner && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 hover:text-destructive"
                              onClick={() => handleRemoveUser(user.id)}
                              disabled={isRemoving}
                              aria-label={`Remove ${displayName}`}
                            >
                              {isRemoving ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <X className="h-3 w-3" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {isOwner && (
                <div className="border-t px-3 py-3">
                  <Command
                    className="relative overflow-visible rounded-md border"
                    shouldFilter={true}
                  >
                    <CommandInput
                      placeholder="Add people by name or email…"
                      className="h-9 text-sm"
                      value={searchQuery}
                      onValueChange={setSearchQuery}
                    />
                    {showAddPeopleDropdown && (
                      <CommandList className="absolute inset-x-0 top-full z-50 mt-1 max-h-[160px] overflow-y-auto rounded-md border bg-popover shadow-md">
                        <CommandEmpty className="py-3 text-center text-xs text-muted-foreground">
                          No users found.
                        </CommandEmpty>
                        <CommandGroup>
                          {availableUsers.map((user) => {
                            const displayName = formatUserName(
                              user.firstName,
                              user.lastName,
                              user.email,
                            );
                            const isAdding = addingUserId === user.id;
                            return (
                              <CommandItem
                                key={user.id}
                                value={`${displayName} ${user.email}`}
                                onSelect={() => handleAddUser(user.id)}
                                disabled={isAdding}
                                className="flex cursor-pointer items-center gap-2.5 py-2"
                              >
                                <div className="flex min-w-0 flex-1 flex-col">
                                  <span className="whitespace-normal text-sm [overflow-wrap:anywhere]">
                                    {displayName}
                                  </span>
                                  {(user.firstName || user.lastName) && (
                                    <span className="whitespace-normal text-[11px] text-muted-foreground [overflow-wrap:anywhere]">
                                      {user.email}
                                    </span>
                                  )}
                                </div>
                                {isAdding && (
                                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
                                )}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    )}
                  </Command>
                </div>
              )}
            </>
          )}
        </PopoverContent>
      </Popover>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmOpen(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-start gap-3.5 text-left">
              <span
                className={cn(
                  "mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-full",
                  confirmIconBubbleClass,
                )}
              >
                <ConfirmIcon className="size-5" />
              </span>
              <div className="space-y-1.5">
                <AlertDialogTitle className="leading-snug">
                  {confirmTitle}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {confirmDescription}
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmVisibility}
              className={buttonVariants({ variant: "default" })}
            >
              {confirmActionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
